"use strict";
// Bayes Modal (BM/MAP) and Expected A Posteriori (EAP) estimators for the
// 2PL Graded Response Model with a standard-normal prior. Matches catR's
// thetaEst(method="BM") and thetaEst(method="EAP") semantics — see Magis &
// Barrada (2017), J. Stat. Soft. 76(c01), §5 example.
//
// Why these and not MLE: MLE on extreme-pattern responses (all-min or
// all-max) diverges to ±∞ in the likelihood, then the optimizer pins θ to
// whatever bound is set. BM regularises with the prior; EAP integrates the
// posterior. Both produce stable θ on small response counts (1–8 items)
// where MLE is essentially useless. Singapore's Promin uses BM; PROMIS
// canonical scoring uses EAP — same posterior, different summary statistic.
Object.defineProperty(exports, "__esModule", { value: true });
exports.estimateThetaML = exports.PRIOR_VAR = exports.PRIOR_SD = exports.PRIOR_MEAN = exports.estimateThetaEAP = exports.estimateThetaBM = void 0;
const utilities_1 = require("./utilities");
const PRIOR_MEAN = 0;
exports.PRIOR_MEAN = PRIOR_MEAN;
const PRIOR_SD = 1;
exports.PRIOR_SD = PRIOR_SD;
const PRIOR_VAR = PRIOR_SD * PRIOR_SD;
exports.PRIOR_VAR = PRIOR_VAR;
const BOUND_LO = -4;
const BOUND_HI = 4;
const NEWTON_MAX_ITER = 50;
const NEWTON_TOL = 1e-6;
const GRID_STEPS = 161; // [-4, +4] in 0.05 increments — matches catR's quadrature grid resolution
/**
 * @param {number} theta
 * @param {import('./types').Response[]} responses
 * @param {import('./types').IRTParams} params
 */
function logPosterior(theta, responses, params) {
    const ll = (0, utilities_1.logLikelihood)(theta, responses, params);
    const priorTerm = -((theta - PRIOR_MEAN) ** 2) / (2 * PRIOR_VAR);
    return ll + priorTerm;
}
/**
 * Central-difference 1st and 2nd derivatives of the log-posterior.
 * Numerical differentiation chosen over closed-form gradient because the
 * GRM probability formulas branch on β1/β2 nullity in 3 ways (one-sided low,
 * one-sided high, middle category) — analytic d/dθ would mean carrying that
 * branching all the way through the chain rule. h=1e-4 keeps cancellation
 * error well below NEWTON_TOL for typical α and β ranges.
 *
 * @param {number} theta
 * @param {import('./types').Response[]} responses
 * @param {import('./types').IRTParams} params
 * @param {number} [h]
 */
function logPosteriorDerivs(theta, responses, params, h = 1e-4) {
    const fPlus = logPosterior(theta + h, responses, params);
    const fMinus = logPosterior(theta - h, responses, params);
    const f0 = logPosterior(theta, responses, params);
    const d1 = (fPlus - fMinus) / (2 * h);
    const d2 = (fPlus - 2 * f0 + fMinus) / (h * h);
    return { d1, d2 };
}
/**
 * @param {number} x
 * @param {number} lo
 * @param {number} hi
 */
function clamp(x, lo, hi) {
    if (x < lo)
        return lo;
    if (x > hi)
        return hi;
    return x;
}
/**
 * Bayes Modal (MAP) θ estimator. Newton-Raphson on the log-posterior with
 * a 161-point grid fallback when curvature is non-negative or step explodes.
 *
 * @param {import('./types').IRTParams} params
 * @param {import('./types').Response[]} responses
 * @returns {number}
 */
function estimateThetaBM(params, responses) {
    if (responses.length === 0)
        return PRIOR_MEAN;
    let theta = 0;
    let converged = false;
    for (let i = 0; i < NEWTON_MAX_ITER; i += 1) {
        const { d1, d2 } = logPosteriorDerivs(theta, responses, params);
        if (!Number.isFinite(d1) || !Number.isFinite(d2) || d2 >= 0)
            break;
        const step = d1 / d2;
        const next = clamp(theta - step, BOUND_LO, BOUND_HI);
        if (Math.abs(next - theta) < NEWTON_TOL) {
            theta = next;
            converged = true;
            break;
        }
        theta = next;
    }
    if (converged)
        return theta;
    // Grid fallback. log-posterior is concave for typical GRM configurations,
    // so this only fires on pathological response patterns or when Newton is
    // initialised in a flat region.
    let bestTheta = 0;
    let bestLp = -Infinity;
    for (let i = 0; i < GRID_STEPS; i += 1) {
        const t = BOUND_LO + ((BOUND_HI - BOUND_LO) * i) / (GRID_STEPS - 1);
        const lp = logPosterior(t, responses, params);
        if (lp > bestLp) {
            bestLp = lp;
            bestTheta = t;
        }
    }
    return bestTheta;
}
exports.estimateThetaBM = estimateThetaBM;
/**
 * Expected A Posteriori (EAP) θ estimator. Trapezoidal quadrature on the
 * same bounded grid, with log-sum-exp normalisation for numerical stability.
 * For PROMIS canonical scoring (mirtCAT / HealthMeasures default).
 *
 * @param {import('./types').IRTParams} params
 * @param {import('./types').Response[]} responses
 * @returns {number}
 */
function estimateThetaEAP(params, responses) {
    if (responses.length === 0)
        return PRIOR_MEAN;
    /** @type {number[]} */
    const grid = new Array(GRID_STEPS);
    /** @type {number[]} */
    const lp = new Array(GRID_STEPS);
    const step = (BOUND_HI - BOUND_LO) / (GRID_STEPS - 1);
    let maxLp = -Infinity;
    for (let i = 0; i < GRID_STEPS; i += 1) {
        grid[i] = BOUND_LO + step * i;
        lp[i] = logPosterior(grid[i], responses, params);
        if (lp[i] > maxLp)
            maxLp = lp[i];
    }
    let num = 0;
    let den = 0;
    for (let i = 0; i < GRID_STEPS; i += 1) {
        const w = i === 0 || i === GRID_STEPS - 1 ? 0.5 : 1.0;
        const p = Math.exp(lp[i] - maxLp);
        num += w * grid[i] * p;
        den += w * p;
    }
    return den > 0 ? num / den : PRIOR_MEAN;
}
exports.estimateThetaEAP = estimateThetaEAP;
// =============================================================================
// Maximum Likelihood (no prior) — Newton-Raphson + grid fallback.
//
// Used for SHAW-CAT / Promin parity testing. Bounded [-5, +5], wider than the
// Bayesian [-4, +4] range, matching catR thetaEst(range=c(-5,5)). On uniform
// extreme response patterns the log-likelihood is monotone in theta and the
// estimator saturates at the bound — desired behaviour for SHAW-CAT
// consistency tests. Replaces the legacy DE-based MLE that ran ~50k cost
// evaluations per estimate; this converges in ~10 Newton iterations or falls
// back to a 201-step grid scan.
// =============================================================================
const ML_BOUND_LO = -5;
const ML_BOUND_HI = 5;
const ML_GRID_STEPS = 201;
/**
 * @param {number} theta
 * @param {import('./types').Response[]} responses
 * @param {import('./types').IRTParams} params
 * @param {number} [h]
 * @returns {{ d1: number, d2: number }}
 */
function logLikelihoodDerivs(theta, responses, params, h = 1e-4) {
    const fPlus = (0, utilities_1.logLikelihood)(theta + h, responses, params);
    const fMinus = (0, utilities_1.logLikelihood)(theta - h, responses, params);
    const f0 = (0, utilities_1.logLikelihood)(theta, responses, params);
    const d1 = (fPlus - fMinus) / (2 * h);
    const d2 = (fPlus - 2 * f0 + fMinus) / (h * h);
    return { d1, d2 };
}
/**
 * Maximum Likelihood estimator. Newton-Raphson on log L(theta) with a grid
 * fallback. No prior. Bounded [-5, +5].
 *
 * @param {import('./types').IRTParams} params
 * @param {import('./types').Response[]} responses
 * @returns {number}
 */
function estimateThetaML(params, responses) {
    if (responses.length === 0)
        return 0;
    let theta = 0;
    let converged = false;
    for (let i = 0; i < NEWTON_MAX_ITER; i += 1) {
        const { d1, d2 } = logLikelihoodDerivs(theta, responses, params);
        if (!Number.isFinite(d1) || !Number.isFinite(d2) || d2 >= 0)
            break;
        const step = d1 / d2;
        const next = clamp(theta - step, ML_BOUND_LO, ML_BOUND_HI);
        if (Math.abs(next - theta) < NEWTON_TOL) {
            theta = next;
            converged = true;
            break;
        }
        theta = next;
    }
    if (converged)
        return theta;
    // Grid fallback: scan the bounded range, pick argmax of log likelihood.
    let bestTheta = 0;
    let bestLl = -Infinity;
    for (let i = 0; i < ML_GRID_STEPS; i += 1) {
        const t = ML_BOUND_LO + ((ML_BOUND_HI - ML_BOUND_LO) * i) / (ML_GRID_STEPS - 1);
        const ll = (0, utilities_1.logLikelihood)(t, responses, params);
        if (ll > bestLl) {
            bestLl = ll;
            bestTheta = t;
        }
    }
    return bestTheta;
}
exports.estimateThetaML = estimateThetaML;
//# sourceMappingURL=bayesModal.js.map